import React, { useEffect, useState } from "react";
import {
    Input,
    Button,
    message,
    Modal,
    Switch,
    Table,
    Tag,
    Space,
    Card,
    Typography,
    Form,
    Tooltip,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ReloadOutlined,
    ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Hexapi from "../../../utility/HexAPI/Hexapi";
import { showConfirm } from "../../../utility/showconfirm";
import ModalCloseButton from "../../../component/ModalCloseButton";


const { Text, Title } = Typography;

const emptyForm = {
    leadsource: "",
    active: true,
};

const LeadSourcePanel = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [leadSourceList, setLeadSourceList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const getLeadSourceList = async () => {
        setLoading(true);
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_LeadSourceMaster_GetList]`,
                queryArr: [],
            };
            const result = await Hexapi(obj);
            setLeadSourceList(result?.[""] || []);
        } catch {
            message.error("Failed to fetch lead sources");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getLeadSourceList();
    }, []);

    const handleToggleActive = async (item, checked) => {
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_LeadSourceMaster_ToggleActive] @RecID='{0}', @Active='{1}'`,
                queryArr: [`${item.recid}`, checked ? 1 : 0],
            };
            await Hexapi(obj);
            message.success(`"${item.leadsource}" ${checked ? "activated" : "deactivated"}`);
            setLeadSourceList((prevList) =>
                prevList.map((s) =>
                    s.recid === item.recid ? { ...s, active: checked ? 1 : 0 } : s
                )
            );
        } catch {
            message.error("Failed to update status");
        }
    };

    // Open Modal for Add or Edit
    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            form.setFieldsValue({
                leadsource: item.leadsource,
                active: !!item.active,
            });
        } else {
            form.setFieldsValue(emptyForm);
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const isEdit = !!editingItem;

            const ok = await showConfirm({
                title: isEdit ? "Update Lead Source?" : "Add Lead Source?",
                content: isEdit
                    ? `Are you sure you want to update "${editingItem.leadsource}"?`
                    : `Are you sure you want to add "${values.leadsource}"?`,
                okText: isEdit ? "Update" : "Add",
                cancelText: "Cancel",
            });

            if (!ok) return;

            setLoading(true);

            if (isEdit) {
                const obj = {
                    query: `[dbo].[CRM_Proc_LeadSourceMaster_Update] @RecID='{0}', @LeadSource='{1}', @Active='{2}'`,
                    queryArr: [
                        `${editingItem.recid}`,
                        `${values.leadsource}`,
                        values.active ? 1 : 0,
                    ],
                };
                const res = await Hexapi(obj);
                if (res?.[""]?.[0]?.updatedrecid !== undefined) {
                    message.success("Lead source updated successfully");
                    setIsModalOpen(false);
                    getLeadSourceList();
                } else {
                    message.error("Update failed");
                }
            } else {
                const obj = {
                    query: `[dbo].[CRM_Proc_LeadSourceMaster_Insert] @LeadSource='{0}', @Active='{1}'`,
                    queryArr: [`${values.leadsource}`, values.active ? 1 : 0],
                };
                const res = await Hexapi(obj);
                if (res?.[""]?.[0]?.command === 1) {
                    message.success("Lead source added successfully");
                    setIsModalOpen(false);
                    getLeadSourceList();
                } else {
                    message.error(res?.[""]?.[0]?.message || "Add failed");
                }
            }
        } catch (err) {
            if (err?.errorFields) return;
            message.error("Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (item) => {
        const ok = await showConfirm({
            title: `Delete "${item.leadsource}"?`,
            content: `This action cannot be undone. "${item.leadsource}" will be permanently removed.`,
            okText: "Delete",
            cancelText: "Cancel",
            danger: true,
        });

        if (!ok) return;

        setLoading(true);
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_LeadSourceMaster_Delete] @RecID='{0}'`,
                queryArr: [`${item.recid}`],
            };
            await Hexapi(obj);
            message.success(`"${item.leadsource}" deleted successfully`);
            getLeadSourceList();
        } catch {
            message.error("Delete failed");
        } finally {
            setLoading(false);
        }
    };

    const filteredLeadSources = leadSourceList.filter((s) =>
        (s.leadsource || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
        {
            title: "Name",
            dataIndex: "leadsource",
            key: "leadsource",
            sorter: (a, b) => (a.leadsource || "").localeCompare(b.leadsource || ""),
            render: (text) => (
                <Space size={12}>
                    <div
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            backgroundColor: "#1250AC",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 12,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                    >
                        {(text || "").charAt(0).toUpperCase()}
                    </div>
                    <Text strong style={{ fontSize: 14 }}>{text}</Text>
                </Space>
            ),
        },
        {
            title: "Status",
            dataIndex: "active",
            key: "active",
            width: 130,
            render: (active, record) => (
                <Space size={8}>
                    <Switch
                        size="small"
                        checked={!!active}
                        onChange={(checked) => handleToggleActive(record, checked)}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {active ? "Active" : "Inactive"}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 110,
            align: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Edit Lead Source">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Lead Source">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, width: "100%", height: "calc(100vh - 48px)", display: "flex", flexDirection: "column" }}>
            <Card
                bordered={false}
                style={{
                    borderRadius: 16,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                }}
                styles={{
                    body: {
                        padding: 24,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Header controls */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 20,
                        gap: 16,
                        flexWrap: "wrap",
                        flexShrink: 0,
                    }}
                >
                    <Space size={12}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                padding: 0,
                            }}
                        />
                        <Title level={4} style={{ margin: 0 }}>
                            Lead Source Master
                        </Title>
                        <Tag style={{ borderRadius: 10, padding: "0 8px" }}>
                            {filteredLeadSources.length} total
                        </Tag>
                    </Space>

                    <Space size={12} style={{ flexWrap: "wrap" }}>
                        <Input
                            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                            placeholder="Search lead sources…"
                            allowClear
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: 240, borderRadius: 8 }}
                        />
                        <Tooltip title="Reload Lead Sources">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={getLeadSourceList}
                                loading={loading}
                                style={{ borderRadius: 8 }}
                            />
                        </Tooltip>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal(null)}
                            style={{ borderRadius: 8 }}
                        >
                            Add Lead Source
                        </Button>
                    </Space>
                </div>

                {/* Main Data Table with Vertical Scrolling */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                    <Table
                        columns={columns}
                        dataSource={filteredLeadSources}
                        rowKey="recid"
                        loading={loading}
                        scroll={{ y: "calc(100vh - 280px)" }}
                        pagination={false}
                    />
                </div>
            </Card>

            {/* Combined Add/Edit Modal */}
            <Modal
                centered
                title={null}
                closable={false}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleSave}
                okText={editingItem ? "Update" : "Add"}
                confirmLoading={loading}
                width={440}
                destroyOnClose
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                    }}
                >
                    <Title level={5} style={{ margin: 0 }}>
                        {editingItem ? "Edit Lead Source" : "Add Lead Source"}
                    </Title>
                    <ModalCloseButton onClose={() => setIsModalOpen(false)} />
                </div>
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="leadsource"
                        label="Lead Source Name"
                        rules={[{ required: true, message: "Please enter lead source name" }]}
                    >
                        <Input placeholder="e.g. Facebook Ads" style={{ borderRadius: 8 }} />
                    </Form.Item>

                    <Form.Item name="active" valuePropName="checked" label="Active Status">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LeadSourcePanel;
